import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import DocumentsForm from "../../WorkOrder/Forms/DocumentsForm";
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import { useMutation } from "@tanstack/react-query";
import { useUpdateWorkOrder } from "../../../hooks/useUpdateWorkOrder";
import { updateWorkOrderApi } from "../../../services/api/workOrder";
import { deleteDigitalAsset, uploadWorkOrderCustomerFiles } from "../../../services/api/digitalAsset";
import { useSectionChanges } from "../../../hooks/useSectionChanges";

const EditCustomerFilesModal = ({ open, close, workOrderCustomerFiles }) => {
  const dispatch = useDispatch();
  const id = useSelector((state) => state.workOrder.workOrderDetails.id);
  const { updateWorkOrder } = useUpdateWorkOrder();

  const customerFilesSection = useSelector((state) => state.workOrder.summary?.customerFiles);
  const customerFiles = customerFilesSection?.files;
  const { hasChanges, onHydrate } = useSectionChanges(customerFilesSection);

  const {
    isPending: isUpdatingLoading,
    mutateAsync: updateWorkOrderApiMutation,
  } = useMutation({
    mutationFn: updateWorkOrderApi,
    onSuccess: () => { },
  });
  const {
    isPending: isUploadWorkOrderCustomerFilesApiLoading,
    error: isUploadWorkOrderCustomerFilesApiError,
    mutateAsync: uploadWorkOrderCustomerFilesMutation,
  } = useMutation({
    mutationFn: uploadWorkOrderCustomerFiles,
  });
    const {
    isPending: isDeleteCustomerFileLoading,
    error: isDeleteCustomerFileError,
    mutateAsync: deleteWorkOrderCustomerFilesMutation,
  } = useMutation({
    mutationFn: deleteDigitalAsset,
  });


  const handleSubmit = async () => {
    try {
      const initialFiles = workOrderCustomerFiles || [];
      const currentFiles = customerFiles || [];

      const currentFileIds = new Set(currentFiles.filter(f => f.id).map(f => f.id));

      const deletedFiles = initialFiles.filter(f => !currentFileIds.has(f.id));
      const newFiles = currentFiles.filter(f => !f.id);
      const keptFiles = currentFiles.filter(f => f.id);

      for (const file of deletedFiles) {
        await deleteWorkOrderCustomerFilesMutation(file.id);
      }

      if (newFiles.length > 0) {
        const formData = new FormData();
        newFiles.forEach(file => formData.append("files", file));
        await uploadWorkOrderCustomerFilesMutation({
          workOrderId: id,
          formData,
        });
      }

      await updateWorkOrderApiMutation({
        id,
        customerFiles: keptFiles
          .filter(f => f.id)
          .map(f => ({ id: parseInt(f.id) })),
      });

      dispatch(
        showToast({
          message: "Customer files updated successfully!",
          type: "success",
        })
      );

      updateWorkOrder();
      close();

    } catch (error) {
      console.error(error);
      dispatch(
        showToast({
          message: "Failed to update customer files.",
          type: "error",
        })
      );
    }
  };

  return (
    <BaseModal className="-top-[200px] overflow-auto" open={open} close={close}>
      <div className="py-7 px-12 xl:w-[1400px] lg:w-[1000px] sm:w-[800px]">
        <h6 className="text-dark font-normal border-b pb-1">
          Edit Customer Files
        </h6>

        <div className="mb-2 mt-3">
          <DocumentsForm
            isEditMode={true}
            workOrderCustomerFiles={workOrderCustomerFiles}
            onHydrate={onHydrate}
          />
        </div>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{
          boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)",
        }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>

        <BaseButton
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || isUpdatingLoading}
          loaderSize="24px"
          isLoading={isUploadWorkOrderCustomerFilesApiLoading || isUpdatingLoading || isDeleteCustomerFileLoading}

        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditCustomerFilesModal;
